import React, { useEffect, useState, useCallback } from 'react';
import { NotebookPen } from 'lucide-react';
import { toast } from 'sonner';
import { notesApi } from '../../lib/notesApi';
import { NotesDrawer } from './NotesDrawer';
import { getCurrentAccount } from '../../lib/auth';

/**
 * Compact icon for page headers. Shows a count badge for open
 * tasks (or total notes if no tasks). Clicking opens the NotesDrawer.
 *
 * Usage:
 *   <NotesIcon pageKey="payments_v2" pageLabel="Payments" />
 *   <NotesIcon pageKey="bills" pageLabel="Bills" accountId={currentAccount?.id} />
 *
 * If `accountId` prop is omitted we fall back to localStorage current_account
 * and re-poll every 1 s for ~10 s after mount so the icon picks up the org
 * once the user selects one. If still no org, the icon stays visible but
 * clicking it asks the user to pick an org first.
 */
export const NotesIcon = ({ pageKey, pageLabel, accountId: forcedAccountId }) => {
  const [open, setOpen] = useState(false);
  const [counts, setCounts] = useState({ total: 0, open_tasks: 0 });

  // Reactive account resolution: prefer prop, fall back to localStorage,
  // then short-poll to pick up a freshly selected org.
  const [resolvedAccountId, setResolvedAccountId] = useState(
    () => forcedAccountId || getCurrentAccount()?.id || null,
  );

  useEffect(() => {
    if (forcedAccountId) {
      setResolvedAccountId(forcedAccountId);
      return undefined;
    }
    const tick = () => {
      const a = getCurrentAccount()?.id || null;
      if (a !== resolvedAccountId) setResolvedAccountId(a);
    };
    tick();
    const iv = setInterval(tick, 1000);
    const to = setTimeout(() => clearInterval(iv), 10000);
    return () => { clearInterval(iv); clearTimeout(to); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forcedAccountId]);

  const refreshCounts = useCallback(async () => {
    if (!resolvedAccountId || !pageKey) return;
    try {
      const c = await notesApi.counts({ account_id: resolvedAccountId, page_key: pageKey });
      setCounts(c || { total: 0, open_tasks: 0 });
    } catch {
      /* non-fatal */
    }
  }, [resolvedAccountId, pageKey]);

  useEffect(() => { refreshCounts(); }, [refreshCounts]);

  // Auto-open if URL has a deep-link from a mention email (?note_id=XYZ#notes)
  useEffect(() => {
    if (typeof window === 'undefined' || !resolvedAccountId) return;
    if (window.location.hash === '#notes') setOpen(true);
  }, [resolvedAccountId]);

  if (!pageKey) return null;

  const badge = counts.open_tasks > 0 ? counts.open_tasks : counts.total;
  const disabled = !resolvedAccountId;

  const handleClick = () => {
    if (disabled) {
      toast.info('Select an organisation first to view notes for this page.');
      return;
    }
    setOpen(true);
  };

  return (
    <>
      <button
        type="button"
        data-testid="notes-icon"
        title={disabled ? 'Select an organisation to view notes' : 'Notes & tasks for this page'}
        onClick={handleClick}
        className={
          'relative inline-flex items-center justify-center rounded-md p-1.5 ' +
          (disabled
            ? 'text-slate-300 hover:bg-slate-50 cursor-help'
            : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900')
        }
      >
        <NotebookPen className="h-[18px] w-[18px]" strokeWidth={2} />
        {!disabled && (
          <span
            data-testid="notes-icon-badge"
            className={
              'absolute -right-1.5 -top-1.5 inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full px-1 text-[10px] font-semibold text-white shadow-sm ' +
              (counts.open_tasks > 0
                ? 'bg-amber-500'
                : badge > 0
                  ? 'bg-slate-500'
                  : 'bg-slate-300')
            }
          >
            {badge > 99 ? '99+' : badge}
          </span>
        )}
      </button>
      {!disabled && (
        <NotesDrawer
          open={open}
          onOpenChange={setOpen}
          accountId={resolvedAccountId}
          pageKey={pageKey}
          pageLabel={pageLabel || pageKey}
          onCountsChange={refreshCounts}
        />
      )}
    </>
  );
};

export default NotesIcon;
